import type { ReactNode, RefObject } from 'react'
import { Braces, Check, Copy, Download, Ellipsis, LineChart, Paperclip, Search, Settings } from 'lucide-react'
import type { Chart } from 'klinecharts'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useIsMobile'
// Relative imports (not the '@/components' barrel) to avoid a cycle now that
// the barrel exports Workbench, which renders this file.
import { Button } from '../Button/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../DropdownMenu/DropdownMenu'
import { WorkbenchTimeframe } from './WorkbenchTimeframe'
import { WorkbenchChartType } from './WorkbenchChartType'
import { WorkbenchCamera } from './WorkbenchCamera'
import { useWorkbenchSnapshot } from './useWorkbenchSnapshot'
import { CHART_TYPES, TIMEFRAME_LABELS, TIMEFRAME_OPTIONS } from './catalog'
import type { ChartTypeId } from './types'

// Top toolbar for the Strategy Design chart. Desktop lays out the chart
// controls in TradingView order — symbol search · timeframe · chart-type ·
// indicators on the left, utility actions (camera, settings, show-state,
// push-to-AI) on the right. Mobile keeps only symbol · backtest-trades ·
// push-to-AI visible and folds everything else into an overflow menu.

export function ToolbarDivider() {
  return <div className="w-px h-5 bg-border mx-0.5 shrink-0" />
}

export interface WorkbenchTopToolbarProps {
  chart: Chart | null
  captureRef: RefObject<HTMLDivElement | null>
  symbol: string
  onOpenSymbolSearch: () => void
  timeframe: string
  onTimeframeChange: (tf: string) => void
  chartType: ChartTypeId
  onChartTypeChange: (id: ChartTypeId) => void
  onOpenIndicators: () => void
  backtestOrdersOn?: boolean
  backtestOrdersDisabled?: boolean
  backtestOrdersTitle?: string
  onToggleBacktestOrders?: () => void
  onOpenSettings: () => void
  onShowState: () => void
  onPush?: () => void
  pushNew?: number
  pushUnpushed?: boolean
  pushDisabled?: boolean
  pushStaged?: boolean
}

export function WorkbenchTopToolbar(props: WorkbenchTopToolbarProps) {
  const isMobile = useIsMobile()
  return isMobile ? <MobileToolbar {...props} /> : <DesktopToolbar {...props} />
}

function ToolbarShell({ children }: { children: ReactNode }) {
  return <div className="shrink-0 h-10 flex items-center gap-1 px-2 border-b border-border bg-background">{children}</div>
}

function SymbolButton({ symbol, onOpenSymbolSearch }: Pick<WorkbenchTopToolbarProps, 'symbol' | 'onOpenSymbolSearch'>) {
  return (
    <button
      id="symbol-search-btn"
      type="button"
      onClick={onOpenSymbolSearch}
      className="inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md hover:bg-muted/60 transition-colors cursor-pointer shrink-0"
    >
      <Search size={14} strokeWidth={1.75} className="text-muted-foreground" />
      <span className="text-label font-semibold text-foreground tracking-tight">{symbol}</span>
    </button>
  )
}

/* Backtest-orders overlay checkbox: checked = the last backtest's trades are
   drawn on the chart. Disabled (with the reason in the label's `title`) when
   there's no finished backtest or its symbol differs from the chart. */
function BacktestToggle({
  label,
  backtestOrdersOn = false,
  backtestOrdersDisabled = true,
  backtestOrdersTitle,
  onToggleBacktestOrders,
}: Pick<
  WorkbenchTopToolbarProps,
  'backtestOrdersOn' | 'backtestOrdersDisabled' | 'backtestOrdersTitle' | 'onToggleBacktestOrders'
> & { label: string }) {
  return (
    <label
      htmlFor="backtest-orders-btn"
      title={backtestOrdersTitle}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors shrink-0 select-none',
        backtestOrdersDisabled
          ? 'text-text-disabled cursor-not-allowed'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer',
      )}
    >
      <Checkbox
        id="backtest-orders-btn"
        checked={backtestOrdersOn}
        disabled={backtestOrdersDisabled}
        onCheckedChange={() => onToggleBacktestOrders?.()}
        className="size-3.5"
      />
      {label}
    </label>
  )
}

/* Push to AI — attach the chart to the chat composer as a one-off, then type +
   send it. A strong primary CTA when there's chart work to attach; a quiet
   "Attached" confirmation while a snapshot is sitting on the composer
   (re-click to refresh it). The paperclip reads "attach", not "upload". The
   unpushed-edit count rides as a corner badge (monochrome — red/green is data). */
function PushCluster({
  onPush,
  pushNew = 0,
  pushUnpushed = false,
  pushDisabled = true,
  pushStaged = false,
}: Pick<WorkbenchTopToolbarProps, 'onPush' | 'pushNew' | 'pushUnpushed' | 'pushDisabled' | 'pushStaged'>) {
  return (
    <div className="relative shrink-0">
      {pushStaged ? (
        <Button
          id="chart-push-btn"
          type="button"
          size="sm"
          variant="secondary"
          onClick={onPush}
          title="Chart added to your next message. Click to refresh with the latest."
          className="h-7 min-h-7 gap-1.5 px-3 text-caption"
        >
          <Check size={11} strokeWidth={2} />
          Attached
        </Button>
      ) : pushUnpushed ? (
        <Button
          id="chart-push-btn"
          type="button"
          size="sm"
          onClick={onPush}
          title="Add the chart to your message to the AI"
          className="h-7 min-h-7 gap-1.5 px-3 text-caption"
        >
          <Paperclip size={11} strokeWidth={2} />
          Add to AI chat
        </Button>
      ) : (
        <Button
          id="chart-push-btn"
          type="button"
          size="sm"
          variant="secondary"
          onClick={onPush}
          disabled={pushDisabled}
          title="Add the chart to your message to the AI"
          className="h-7 min-h-7 gap-1.5 px-3 text-caption"
        >
          <Paperclip size={11} strokeWidth={2} />
          Add to AI chat
        </Button>
      )}
      {pushNew > 0 && (
        <span
          aria-label={`${pushNew} unpushed change${pushNew === 1 ? '' : 's'}`}
          className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-zinc-950 text-[10px] font-semibold tabular-nums shadow-sm pointer-events-none flex items-center justify-center"
        >
          {pushNew}
        </span>
      )}
    </div>
  )
}

function DesktopToolbar(props: WorkbenchTopToolbarProps) {
  const { chart, captureRef, timeframe, onTimeframeChange, chartType, onChartTypeChange, onOpenIndicators, onOpenSettings, onShowState } = props
  return (
    <ToolbarShell>
      {/* left group: market + chart-shape controls */}
      <SymbolButton {...props} />
      <ToolbarDivider />
      <WorkbenchTimeframe value={timeframe} onChange={onTimeframeChange} />
      <ToolbarDivider />
      <WorkbenchChartType value={chartType} onChange={onChartTypeChange} />
      <ToolbarDivider />
      <button
        id="indicators-btn"
        type="button"
        onClick={onOpenIndicators}
        className={cn(
          'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors cursor-pointer',
          'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
        )}
      >
        <LineChart size={14} strokeWidth={1.75} />
        Indicators
      </button>
      <BacktestToggle label="Overlay backtest" {...props} />

      {/* right group: utility actions */}
      <div className="ml-auto flex items-center gap-1">
        <WorkbenchCamera chart={chart} captureRef={captureRef} />
        <button
          type="button"
          onClick={onOpenSettings}
          aria-label="Chart settings"
          className={cn(
            'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer',
            'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Settings size={15} strokeWidth={1.75} />
        </button>
        {/* Dev-only aid: ships hidden. Re-enable from devtools with
            `display: inline-flex` on [data-debug="show-state"]. */}
        <button
          type="button"
          onClick={onShowState}
          aria-label="Show State"
          data-debug="show-state"
          className={cn(
            'hidden items-center gap-1.5 h-7 px-2.5 rounded-md text-label transition-colors cursor-pointer',
            'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Braces size={14} strokeWidth={1.75} />
          Show State
        </button>
        <PushCluster {...props} />
      </div>
    </ToolbarShell>
  )
}

function MobileToolbar(props: WorkbenchTopToolbarProps) {
  const { chart, captureRef, timeframe, onTimeframeChange, chartType, onChartTypeChange, onOpenIndicators, onOpenSettings, onShowState } = props
  const snapshot = useWorkbenchSnapshot(chart, captureRef)
  const tfLabel = (TIMEFRAME_LABELS as Record<string, string>)[timeframe] ?? timeframe
  return (
    <ToolbarShell>
      <SymbolButton {...props} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="More tools"
            className={cn(
              'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer shrink-0',
              'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            <Ellipsis size={16} strokeWidth={1.75} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <span className="flex-1">Timeframe</span>
              <span className="text-muted-foreground tabular-nums mr-1">{tfLabel}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={timeframe} onValueChange={onTimeframeChange}>
                {TIMEFRAME_OPTIONS.map((tf) => (
                  <DropdownMenuRadioItem key={tf} value={tf} className="tabular-nums">
                    {TIMEFRAME_LABELS[tf]}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Chart type</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={chartType} onValueChange={(v) => onChartTypeChange(v as ChartTypeId)}>
                {CHART_TYPES.map((t) => (
                  <DropdownMenuRadioItem key={t.id} value={t.id}>
                    {t.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => onOpenIndicators()}>
            <LineChart size={14} strokeWidth={1.75} />
            Indicators…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void snapshot.save()}>
            <Download size={14} strokeWidth={1.75} />
            Save chart image
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => void snapshot.copy()}>
            <Copy size={14} strokeWidth={1.75} />
            Copy chart image
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onOpenSettings()}>
            <Settings size={14} strokeWidth={1.75} />
            Chart settings…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <BacktestToggle label="Backtest" {...props} />
      <div className="ml-auto flex items-center gap-1">
        {/* Dev-only aid, ships hidden (same devtools re-enable as desktop). */}
        <button
          type="button"
          onClick={onShowState}
          aria-label="Show State"
          data-debug="show-state"
          className="hidden items-center gap-1.5 h-7 px-2 rounded-md text-label text-muted-foreground cursor-pointer"
        >
          <Braces size={14} strokeWidth={1.75} />
        </button>
        <PushCluster {...props} />
      </div>
    </ToolbarShell>
  )
}
